import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clinicalNotesAPI, patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDateTime } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ArrowLeft,
  Mic,
  MicOff,
  Loader2,
  Sparkles,
  Save,
  Check,
  X,
  FileText,
  Clock,
  User,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClinicalNotesPage() {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const { hasPermission } = useAuth();
  
  const [patient, setPatient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('create');
  
  // Dictation state
  const [isRecording, setIsRecording] = useState(false);
  const [dictationText, setDictationText] = useState('');
  const [noteType, setNoteType] = useState('soap');
  const [generating, setGenerating] = useState(false);
  const [generatedNote, setGeneratedNote] = useState(null);
  const [editedNote, setEditedNote] = useState('');
  
  // Web Speech API
  const recognitionRef = useRef(null);

  useEffect(() => {
    fetchData();
    setupSpeechRecognition();
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [patientId]);

  const fetchData = async () => {
    try {
      const [patientRes, notesRes] = await Promise.all([
        patientsAPI.getById(patientId),
        clinicalNotesAPI.getAll({ patient_id: patientId }),
      ]);
      setPatient(patientRes.data);
      setNotes(notesRes.data);
    } catch (err) {
      toast.error('Failed to load data');
      navigate('/patients');
    }
    setLoading(false);
  };

  const setupSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setDictationText(prev => prev + finalTranscript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please enable microphone permissions.');
        }
      };

      recognitionRef.current.onend = () => {
        if (isRecording) {
          recognitionRef.current.start();
        }
      };
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
      toast.info('Listening... Speak clearly into your microphone');
    }
  };

  const handleGenerateNote = async () => {
    if (!dictationText.trim()) {
      toast.error('Please enter or dictate some text first');
      return;
    }

    setGenerating(true);
    try {
      const response = await clinicalNotesAPI.generate({
        patient_id: patientId,
        note_type: noteType,
        dictation_text: dictationText,
      });
      
      setGeneratedNote(response.data);
      setEditedNote(response.data.ai_generated_note);
      toast.success('Clinical note generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate note');
    }
    setGenerating(false);
  };

  const handleApproveNote = async () => {
    if (!generatedNote) return;

    try {
      await clinicalNotesAPI.approve(generatedNote.id, {
        note_id: generatedNote.id,
        edited_note: editedNote,
        approve: true,
      });
      
      toast.success('Clinical note approved and finalized!');
      setDictationText('');
      setGeneratedNote(null);
      setEditedNote('');
      fetchData();
      setActiveTab('history');
    } catch (err) {
      toast.error('Failed to approve note');
    }
  };

  const handleRejectNote = async () => {
    if (!generatedNote) return;

    try {
      await clinicalNotesAPI.approve(generatedNote.id, {
        note_id: generatedNote.id,
        edited_note: editedNote,
        approve: false,
      });
      
      toast.info('Note discarded');
      setGeneratedNote(null);
      setEditedNote('');
    } catch (err) {
      toast.error('Failed to discard note');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-yellow-100 text-yellow-800',
      finalized: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="clinical-notes-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/patients/${patientId}`)} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI Clinical Documentation</h1>
          <p className="text-muted-foreground">
            Patient: {patient?.first_name} {patient?.last_name} ({patient?.patient_id})
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="create" data-testid="tab-create">
            <Sparkles className="w-4 h-4 mr-2" />
            Create Note
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <FileText className="w-4 h-4 mr-2" />
            Note History ({notes.length})
          </TabsTrigger>
        </TabsList>

        {/* Create Note Tab */}
        <TabsContent value="create" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Dictation Input */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Voice Dictation</CardTitle>
                <CardDescription>
                  Click the microphone to start dictating or type your notes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Note Type</Label>
                  <Select value={noteType} onValueChange={setNoteType}>
                    <SelectTrigger data-testid="note-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="soap">SOAP Note</SelectItem>
                      <SelectItem value="narrative">Narrative Note</SelectItem>
                      <SelectItem value="progress">Progress Note</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Dictation / Notes</Label>
                  <div className="relative">
                    <Textarea
                      value={dictationText}
                      onChange={(e) => setDictationText(e.target.value)}
                      placeholder="Click the microphone to start voice dictation, or type your clinical notes here..."
                      rows={12}
                      className="pr-12"
                      data-testid="dictation-input"
                    />
                    <Button
                      type="button"
                      variant={isRecording ? 'destructive' : 'secondary'}
                      size="icon"
                      className="absolute bottom-3 right-3"
                      onClick={toggleRecording}
                      data-testid="mic-btn"
                    >
                      {isRecording ? (
                        <MicOff className="h-4 w-4" />
                      ) : (
                        <Mic className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {isRecording && (
                    <p className="text-sm text-red-500 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      Recording... Speak clearly
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleGenerateNote}
                  disabled={generating || !dictationText.trim()}
                  className="w-full gap-2"
                  data-testid="generate-note-btn"
                >
                  {generating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Generate AI Clinical Note
                </Button>
              </CardContent>
            </Card>

            {/* Generated Note Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">AI-Generated Note</CardTitle>
                <CardDescription>
                  Review and edit the generated note before approving
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {generatedNote ? (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary">{generatedNote.note_type.toUpperCase()}</Badge>
                      <Badge className={getStatusBadge(generatedNote.status)}>
                        {generatedNote.status}
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      <Label>Edit Note (if needed)</Label>
                      <Textarea
                        value={editedNote}
                        onChange={(e) => setEditedNote(e.target.value)}
                        rows={12}
                        className="font-mono text-sm"
                        data-testid="edited-note-input"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={handleApproveNote}
                        className="flex-1 gap-2"
                        data-testid="approve-note-btn"
                      >
                        <Check className="h-4 w-4" />
                        Approve & Finalize
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleRejectNote}
                        className="gap-2"
                        data-testid="reject-note-btn"
                      >
                        <X className="h-4 w-4" />
                        Discard
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>Generated note will appear here</p>
                    <p className="text-sm mt-2">
                      Dictate or type your notes, then click "Generate AI Clinical Note"
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Clinical Note History</CardTitle>
                <CardDescription>All clinical notes for this patient</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {notes.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No clinical notes yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {notes.map((note) => (
                      <Card key={note.id} className="card-interactive" data-testid={`note-${note.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">{note.note_type.toUpperCase()}</Badge>
                              <Badge className={getStatusBadge(note.status)}>
                                {note.status}
                              </Badge>
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDateTime(note.created_at)}
                            </div>
                          </div>
                          
                          <div className="bg-muted p-3 rounded-lg text-sm whitespace-pre-wrap font-mono">
                            {note.final_note || note.ai_generated_note}
                          </div>
                          
                          <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Created by {note.created_by_name}
                            {note.approved_by_name && ` • Approved by ${note.approved_by_name}`}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
